import { toggleAttribute, toggleHidden } from '../shared/util'
import { Controller } from '@hotwired/stimulus'
import { formatHours } from '../shared/util/format'
import i18n from '../../i18n'

export default class extends Controller {
  static targets = [
    'summary',
    'endDateToggle',
    'startDateField',
    'endDateField',
    'subHourBankField',
    'addedHoursTag',
    'hoursField',
    'subHourBankProgress',
    'subHourBankProgressBar',
    'subHourBankName',
    'hourBankName',
    'hourBankProgressBar',
    'subHourBankTooManyHours',
    'hourBankTooManyHours',
    'subHourBankFraction',
    'hourBankFraction',
    'addingTooManyHoursText',
    'addingSummaryText',
  ]
  static values = {
    profileId: Number,
  }

  connect() {
    const vm = this

    $(this.subHourBankFieldTarget)
      .select2({
        language: new URLSearchParams(window.location.search).get('locale'),
        ajax: {
          url: '/search/sub_hour_banks',
          dataType: 'json',
          delay: 250,
          data(params) {
            return {
              profile_id: vm.profileIdValue,
              q: params.term,
              page: params.page,
            }
          },
          processResults(data, params) {
            const paramsPage = params.page || 1

            const results = []

            data.items.forEach((item) => {
              const defaultChild = item.children.find((c) => c.category_default)
              const realChildren = item.children.filter((c) => !c.category_default)

              if (defaultChild) {
                // Flatten the group into individually selectable items.
                // The default acts as the "select the category directly" option.
                // Real subcategories follow as indented items.
                results.push({
                  id: defaultChild.id,
                  text: item.text,
                  isCategoryDefault: true,
                })
                realChildren.forEach((subhour_bank) => {
                  results.push({
                    id: subhour_bank.id,
                    text: subhour_bank.name,
                    isCategoryDefaultSubcategory: true,
                  })
                })
              } else {
                results.push({
                  id: item.id,
                  text: item.text,
                  children: item.children.map((subhour_bank) => ({
                    id: subhour_bank.id,
                    text: subhour_bank.name,
                  })),
                })
              }
            })

            return {
              results,
              pagination: {
                more: paramsPage * data.per_page < data.total_count,
              },
            }
          },
        },
        templateResult(item) {
          if (item.isCategoryDefaultSubcategory) {
            const el = document.createElement('span')

            el.style.paddingLeft = '1.5em'
            el.textContent = item.text

            return el
          }

          return item.text
        },
        placeholder: gon.t.prompt,
        width: '100%',
      })
      .on('change', () => this.fillSummary())
    this.updateFieldsState()
    this.fillSummary()
  }

  updateFieldsState() {
    const checked = this.endDateToggleTarget.checked

    toggleHidden(this.endDateFieldTarget, !checked)

    this.updateFieldRequiredAttribute(this.startDateFieldTarget, checked)
    this.updateFieldRequiredAttribute(this.endDateFieldTarget, checked)

    if (!checked) {
      const input = this.endDateFieldTarget.querySelector('input')

      input.value = null
      input.dispatchEvent(new Event('change'))
    }
  }

  updateFieldRequiredAttribute(fieldTarget, required) {
    const inputElement = fieldTarget.querySelector('input')

    toggleAttribute(inputElement, 'required', required)
    inputElement.dispatchEvent(new Event('required'))
  }

  fillSummary() {
    if (!this.hoursFieldTarget.value || !this.subHourBankFieldTarget.value) {
      this.summaryTarget.hidden = true

      return
    }
    this.summaryTarget.hidden = false
    this.addedHoursTagTarget.textContent = `+${formatHours(this.hoursFieldTarget.value)}`
    const subHourBank = gon.sub_hour_banks[this.subHourBankFieldTarget.value]
    const subHourBankValue = subHourBank.done_hours + Number(this.hoursFieldTarget.value)
    const hourBank = gon.hour_banks[subHourBank.category_id]
    const hourBankDoneHours =
      Object.values(gon.sub_hour_banks)
        .filter((innerSubHourBank) => innerSubHourBank.category_id === subHourBank.category_id)
        .reduce((acc, val) => acc + val.done_hours, 0) + Number(this.hoursFieldTarget.value)

    const isDefault = subHourBank.category_default

    this.subHourBankNameTarget.textContent = subHourBank.name
    this.hourBankNameTarget.textContent = subHourBank.category_name
    this.addingTooManyHoursTextTarget.hidden = isDefault || subHourBankValue - subHourBank.hours <= 0
    this.addingTooManyHoursTextTarget.textContent = i18n.template(gon.t.adding_too_many_hours, {
      sub_hour_bank_name: subHourBank.name,
      hours: formatHours(subHourBankValue - subHourBank.hours),
    })

    this.addingSummaryTextTarget.textContent = i18n.template(gon.t.adding_summary, {
      sub_hour_bank_name: subHourBank.name,
      hours: formatHours(this.hoursFieldTarget.value),
      hour_bank_name: subHourBank.category_name,
    })

    this.subHourBankProgressTarget.hidden = isDefault
    if (!isDefault) {
      this.setProgressBarAndText(this.subHourBankProgressBarTarget, this.subHourBankTooManyHoursTarget, this.subHourBankFractionTarget, subHourBankValue, subHourBank.hours)
    }

    this.setProgressBarAndText(this.hourBankProgressBarTarget, this.hourBankTooManyHoursTarget, this.hourBankFractionTarget, hourBankDoneHours, hourBank.hours)
  }

  setProgressBarAndText(progressBarTarget, textTarget, fractionTarget, value, max) {
    const surplusHours = value - max

    progressBarTarget.value = value
    progressBarTarget.max = max

    fractionTarget.querySelector('.worked-hours').textContent = formatHours(value, ':')
    fractionTarget.querySelector('.planned-hours').textContent = formatHours(max, ':')
    textTarget.parentElement.hidden = surplusHours <= 0
    textTarget.textContent = i18n.template(gon.t.too_many_hours, { hours: formatHours(surplusHours) })
  }
}
